/* eslint-disable react-refresh/only-export-components */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import BrandedFeedPreview from './BrandedFeedPreview'
import { OverlayPortal } from './common/overlays/OverlayFoundation'
import styles from './PostHoverPreview.module.css'

const OPEN_DELAY_MS = 350
const CURSOR_OFFSET = 16
const VIEWPORT_MARGIN = 16

// Anchored to the cursor rather than a trigger element, so the preview tracks
// the pointer across an entire row's content zone instead of pinning to one
// spot. Flips to whichever side of the cursor still fits inside the viewport.
function computePosition(point, size) {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const fitsRight = point.x + CURSOR_OFFSET + size.width <= viewportWidth - VIEWPORT_MARGIN
  const fitsBelow = point.y + CURSOR_OFFSET + size.height <= viewportHeight - VIEWPORT_MARGIN
  const preferredLeft = fitsRight
    ? point.x + CURSOR_OFFSET
    : point.x - CURSOR_OFFSET - size.width
  const preferredTop = fitsBelow
    ? point.y + CURSOR_OFFSET
    : point.y - CURSOR_OFFSET - size.height
  const maxLeft = Math.max(VIEWPORT_MARGIN, viewportWidth - VIEWPORT_MARGIN - size.width)
  const maxTop = Math.max(VIEWPORT_MARGIN, viewportHeight - VIEWPORT_MARGIN - size.height)

  return {
    left: Math.min(Math.max(preferredLeft, VIEWPORT_MARGIN), maxLeft),
    top: Math.min(Math.max(preferredTop, VIEWPORT_MARGIN), maxTop),
  }
}

// Cursor-anchored, portaled preview of a feed post shown while an operator
// hovers a Manage-posts row. Renders through the same BrandedFeedPreview the
// composer uses so the two can never drift apart.
//
// `anchor` is owned by the caller (see useHoverPreviewAnchor below). The open
// delay lives entirely in this component: once armed by a non-null anchor it
// mounts after OPEN_DELAY_MS and then repositions on every anchor update with
// no further delay, and a null anchor tears everything down instantly.
export default function PostHoverPreview({ anchor, branding, item }) {
  const [visible, setVisible] = useState(false)
  const [style, setStyle] = useState(null)
  const surfaceRef = useRef(null)
  const anchorRef = useRef(anchor)
  anchorRef.current = anchor
  const hasAnchor = anchor != null

  useEffect(() => {
    if (!hasAnchor) {
      setVisible(false)
      return undefined
    }
    const timeoutId = window.setTimeout(() => setVisible(true), OPEN_DELAY_MS)
    return () => window.clearTimeout(timeoutId)
    // hasAnchor (not anchor) is intentional: it only flips on enter/leave, so
    // cursor movement while hovering never re-arms this timer.
  }, [hasAnchor])

  const updatePosition = useCallback(() => {
    const surface = surfaceRef.current
    const currentAnchor = anchorRef.current
    if (!surface || !currentAnchor) return
    setStyle(computePosition(currentAnchor, surface.getBoundingClientRect()))
  }, [])

  useLayoutEffect(() => {
    if (!visible || !anchor) {
      setStyle(null)
      return
    }
    updatePosition()
  }, [visible, anchor, updatePosition])

  // The lazy feed card can grow after the overlay's first layout. Recompute
  // whenever its rendered size changes so an initially short loading state
  // cannot leave the completed preview clipped below the viewport.
  useLayoutEffect(() => {
    const surface = surfaceRef.current
    if (!visible || !surface) return undefined

    let frameId = null
    const schedulePosition = () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId)
      frameId = window.requestAnimationFrame(updatePosition)
    }
    const observer = typeof ResizeObserver === 'function'
      ? new ResizeObserver(schedulePosition)
      : null
    observer?.observe(surface)
    window.addEventListener('resize', schedulePosition)

    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', schedulePosition)
      if (frameId !== null) window.cancelAnimationFrame(frameId)
    }
  }, [visible, updatePosition])

  if (!anchor || !visible) return null

  return (
    <OverlayPortal>
      <div
        ref={surfaceRef}
        role="tooltip"
        aria-hidden="true"
        className={styles.preview}
        style={style ? { left: `${style.left}px`, top: `${style.top}px` } : { left: 0, top: 0, opacity: 0 }}
      >
        <BrandedFeedPreview
          branding={branding}
          item={item}
          chrome={false}
          initialMode="card"
          showCardHeader={false}
          testId="post-hover-preview"
        />
      </div>
    </OverlayPortal>
  )
}

// Shared cursor-tracking state for hover-preview triggers. Wire `show`/`move`
// to the trigger zone's pointer-enter/pointer-move handlers and `hide` to its
// leave/blur so callers never re-implement viewport-coordinate bookkeeping.
export function useHoverPreviewAnchor() {
  const [anchor, setAnchor] = useState(null)
  const setFromEvent = useCallback((event) => {
    setAnchor({ x: event.clientX, y: event.clientY })
  }, [])
  const hide = useCallback(() => setAnchor(null), [])
  return { anchor, show: setFromEvent, move: setFromEvent, hide }
}
